import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  People,
  ShoppingCart,
  Settings,
  Business,
  Store as StoreIcon,
  AccountBalance as TaxIcon,
  AccountCircle,
  Logout,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Folder as CatalogIcon,
  Campaign as PromotionIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useNavigate, useLocation } from 'react-router-dom';

const drawerWidth = 240;
const collapsedDrawerWidth = 0; // Width when sidebar is collapsed

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, hasPermission } = useAuth();
  const { isNavigationCollapsed, toggleNavigationCollapse } = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' }, // No permission needed for dashboard
    { text: 'Catalog', icon: <CatalogIcon />, path: '/catalog', permission: 'read-categories' },
    { text: 'Products', icon: <Inventory />, path: '/products', permission: 'read-products' },
    { text: 'Promotions', icon: <PromotionIcon />, path: '/promotions', permission: 'read-coupons' },
    { text: 'Users', icon: <People />, path: '/users', permission: 'read-users' },
    { text: 'Orders', icon: <ShoppingCart />, path: '/orders', permission: 'read-orders' },
    { text: 'Store Settings', icon: <StoreIcon />, path: '/store-settings', permission: 'read-settings' },
    { text: 'Tax Settings', icon: <TaxIcon />, path: '/tax-settings', permission: 'read-settings' },
    { text: 'Content Settings', icon: <Settings />, path: '/settings', permission: 'read-settings' },
    { text: 'Image Settings', icon: <ImageIcon />, path: '/image-settings', permission: 'read-settings' },
    { text: 'Business Settings', icon: <Business />, path: '/business-settings', permission: 'read-settings' },
  ];

  const filteredMenuItems = menuItems.filter(item => {
    const hasAccess = !item.permission || hasPermission(item.permission);return hasAccess;
  });

  const currentDrawerWidth = isNavigationCollapsed ? collapsedDrawerWidth : drawerWidth;

  const drawer = !isNavigationCollapsed ? (
    <div style={{ backgroundColor: '#f5f5f5', height: '100vh' }}>
      <Toolbar sx={{ backgroundColor: '#1976d2', color: 'white' }}>
        <Typography variant="h6" noWrap component="div">
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ backgroundColor: '#fff' }}>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  ) : null;

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle navigation"
            edge="start"
            onClick={toggleNavigationCollapse}
            sx={{ mr: 2, display: { sm: 'block' } }}
          >
            {isNavigationCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
          
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { xs: 'block', sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Shopping Cart Admin
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={user?.roles?.[0]?.name || 'User'} 
              size="small" 
              color="secondary" 
            />
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>
                <AccountCircle sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {!isNavigationCollapsed && (
        <Box
          component="nav"
          sx={{ 
            width: { sm: currentDrawerWidth }, 
            flexShrink: { sm: 0 },
            transition: 'width 0.3s ease',
          }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: 'block !important',
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: currentDrawerWidth,
                position: 'fixed',
                zIndex: 1200,
                transition: 'width 0.3s ease',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      )}
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;